import { useCallback, useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Creators as GraphqlCreators } from "model/graphql"
import { get } from "lodash"
import { OK, LOADING, DataStatus } from "helpers/data-statuses"
import { POSITIONS_SUBQUERY } from "hooks/rebalancer"
import { transformFormToConfiguration } from "connected-components/optimize-experiments/components/helpers/configuration"
import { SETUP_GOALS } from "connected-components/optimize-experiments/constants"
import { SelectedInstrument, SelectedTypedInstrument } from "helpers/types/instruments/selected"
import { Uuid } from "helpers/types/uuid"
import { IsoDate } from "helpers/types/dates"
import {
  OptimizedPositionSet,
  OptimizationConfiguration,
  OptimizationConstraints,
  OptimizationObjective,
} from "connected-components/optimize-experiments/hooks/types"
import { SetupSection, RulesSection } from "connected-components/optimize-experiments/configuration-sections"

const OPTIMIZATION_QUERY = `
query ($modelId: String!, $on: Date!, $positionSet: PositionSetInput!, $base: [PositionSetInput!], $constraints: OptimizationConstraints!, $objectives: [OptimizationObjective]!) {
  model(id: $modelId) {
    optimization(
      positionSet: $positionSet
      base: $base
      on: [$on]
      objective:$objectives
      constraints:$constraints
    ) {
      positions {
        dates {
          ${POSITIONS_SUBQUERY}
        }
      }
    }
  }
}`

interface PositionSetInput {
  type: string
  id: string | Uuid
  experimentId?: string
}

interface UseOptimizationOptions {
  overrideConfiguration?: OptimizationConfiguration | null
  setup?: SetupSection | null
  rules?: RulesSection | null
}

interface OptimizationResult {
  id: string
  status: DataStatus
  data?: {
    model?: {
      optimization?: {
        positions?: OptimizedPositionSet
      }
    }
  }
}

interface ReduxState {
  global: {
    selected: {
      account?: string
    }
    data: {
      optimizationResult?: OptimizationResult
    }
  }
}

interface UseOptimizationReturn {
  runOptimization: () => void
  optimizationStatus: DataStatus | null
  optimizedPositionSet: OptimizedPositionSet | null
  getConfiguration: () => OptimizationConfiguration
}

export const useOptimization = (
  instrument: SelectedTypedInstrument | null,
  modelId: string,
  date: IsoDate,
  base: SelectedInstrument | PositionSetInput[] | null,
  options: UseOptimizationOptions = {},
): UseOptimizationReturn => {
  const { overrideConfiguration = null, setup = null, rules = null } = options
  const [isOptimizing, setIsOptimizing] = useState<boolean>(false)
  const [optimizedPositionSet, setOptimizedPositionSet] = useState<OptimizedPositionSet | null>(null)
  const [optimizationStatus, setOptimizationStatus] = useState<DataStatus | null>(null)

  const dispatch = useDispatch()
  const account = useSelector((state: ReduxState) => state.global.selected.account)
  const currentOptimizationId = useRef<string | null>(null)

  const optimizationResult = useSelector((state: ReduxState): OptimizationResult | null => {
    if (!currentOptimizationId.current) return null
    const result = state.global.data.optimizationResult
    return result?.id === currentOptimizationId.current ? result : null
  })

  const defaultSetup: SetupSection = { goal: SETUP_GOALS.MINIMIZE_TOTAL_RISK }
  const defaultRules: RulesSection = { maxTurnover: 100 }

  const configuration: OptimizationConfiguration = overrideConfiguration || transformFormToConfiguration(
    date,
    setup || defaultSetup,
    rules || defaultRules,
  )
  const { on, constraints, objectives }: {
    on: IsoDate
    constraints: Partial<OptimizationConstraints>
    objectives: Partial<OptimizationObjective>[]
  } = configuration

  useEffect(() => {
    if (!optimizationResult || !currentOptimizationId.current) return

    if (optimizationResult.status === OK) {
      const positions = get(optimizationResult, ["data", "model", "optimization", "positions"])
      if (positions) {
        setOptimizedPositionSet(positions)
        setOptimizationStatus(OK)
        setIsOptimizing(false)
      }
    } else {
      setOptimizationStatus(optimizationResult.status)
      setIsOptimizing(false)
    }
  }, [optimizationResult])

  const runOptimization = useCallback(() => {
    if (!instrument || !modelId || !date || isOptimizing) return

    const timestamp = Date.now()
    const positionSetInput: PositionSetInput = {
      type: instrument.type,
      id: instrument.id,
      ...(("experimentId" in instrument && instrument.experimentId) && { experimentId: instrument.experimentId }),
    }
    const id = `optimizationResult-${date}-${modelId}-${JSON.stringify(positionSetInput)}-${timestamp}`

    currentOptimizationId.current = id
    setIsOptimizing(true)
    setOptimizationStatus(LOADING)
    setOptimizedPositionSet(null)

    dispatch(GraphqlCreators.load(
      "optimizationResult",
      id,
      OPTIMIZATION_QUERY,
      {
        modelId,
        on,
        positionSet: positionSetInput,
        base,
        objectives,
        constraints,
      },
      account,
    ))
  }, [instrument, modelId, date, base, isOptimizing, dispatch, account, objectives, constraints, on])

  const getConfiguration = useCallback((): OptimizationConfiguration => {
    return {
      on: date,
      objectives,
      constraints,
    }
  }, [date, constraints, objectives])

  return {
    runOptimization,
    optimizationStatus: isOptimizing ? LOADING : optimizationStatus,
    optimizedPositionSet,
    getConfiguration,
  }
}
